import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AlertService,
  CardComponent,
  CardContentComponent,
  IconComponent,
  LoadingComponent,
} from '@gains-com/ui-angular';
import { CurrencyPipe, DecimalPipe as NumberPipe, formatCurrency } from '@angular/common';
import {
  AnalyticsEvent,
  decimal,
  generateId,
  DOLLAR_TO_GOLD_CONVERSION as GOLD_CONVERSION_RATE,
  DOLLAR_TO_SWEEP_CONVERSION as SWEEPS_CONVERSION_RATE,
  isBoostRewardType,
  PaymentMethod,
  PaymentMethodEntityTypeId,
  PromotionRewardType,
} from '@gains-com/shared';
import { PurchaseDialogHeaderComponent } from '../purchase-dialog-header/purchase-dialog-header.component.js';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { Analytics } from '../../../../core/analytics/analytics.service.js';
import {
  CoinflowCardMetadata,
  PURCHASE_LIMIT_REACHED_MESSAGE,
  PurchasePaymentMethod,
  PurchaseSelection,
} from '../../common.js';
import { DecimalPipe } from '../../../../pipes/decimal.pipe.js';
import {
  AccountLimitsViewService,
} from '../../../../core/view-states/account-limits-view.service.js';
import {
  FirstTimeBoostTier,
  PromotionsView,
  UserPromotionRewardVM,
} from '../../../../core/view-states/promotions-view.service.js';
import { Decimal } from 'decimal.js';
import { HttpClient } from '@angular/common/http';
import { catchError, from, lastValueFrom, map, of, switchMap, take } from 'rxjs';
import { environment } from '../../../../../environments/environment.js';
import { PaymentMethodsService } from '../../../../core/payment-methods.js';

import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';
import { GoldCoinsIconComponent } from '../../../coins/gold-coins-icon.component.js';
import { SweepsCoinsIconComponent } from '../../../coins/sweeps-coins-icon.component.js';
import { PaymentMethodsViewService } from '../../../../core/view-states/payment-methods-view.service.js';
import { PurchaseBoostBannerComponent } from '../purchase-boost-banner/purchase-boost-banner.component.js';
import { FirstTimeBoostViewComponent } from '../first-time-boost-view/first-time-boost-view.component.js';
import { RegularBoostViewComponent } from '../regular-boost-view/regular-boost-view.component.js';
import { SweepsVisibleDirective } from '../../../../directives/sweeps-visible.directive.js';
import { DeviceModeViewService } from '../../../../core/view-states/device-mode-view.service.js';

const PRESET_AMOUNTS = [25, 50, 100, 500] as const;
const FIRST_TIME_PRESET_AMOUNTS = [10, 25, 100, 1000] as const;
const MAX_PURCHASE_AMOUNT_USD = 10_000;

const FTP_TIER_METAS = [
  { label: 'VALUE', color: '#30D158' },
  { label: 'POPULAR', color: '#A479FB' },
  { label: 'ELITE', color: '#0091FF' },
  { label: 'MAJOR', color: '#FF82FA' },
] as const;

@Component({
  selector: 'app-buy-coins',
  imports: [
    CardComponent,
    CardContentComponent,
    DecimalPipe,
    CurrencyPipe,
    PurchaseDialogHeaderComponent,
    IconComponent,
    ReactiveFormsModule,
    LoadingComponent,
    NumberPipe,
    GoldCoinsIconComponent,
    SweepsCoinsIconComponent,
    PurchaseBoostBannerComponent,
    FirstTimeBoostViewComponent,
    RegularBoostViewComponent,
    SweepsVisibleDirective,
  ],
  templateUrl: './buy-coins.component.html',
  styleUrls: ['../styles.scss', './buy-coins.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyCoinsComponent {
  private readonly _userView = inject(UserViewService);
  private readonly _analytics = inject(Analytics);
  private readonly _accountLimitsView = inject(AccountLimitsViewService);
  private readonly _promotionsView = inject(PromotionsView);
  private readonly _logger = inject(LOGGER_FACTORY).create('BuyCoins');
  private readonly _http = inject(HttpClient);
  private readonly _alertService = inject(AlertService);
  private readonly _paymentMethodsView = inject(PaymentMethodsViewService);
  private readonly _paymentMethodsService = inject(PaymentMethodsService);
  private readonly _deviceModeView = inject(DeviceModeViewService);

  // Input/Output Properties
  noSCInitialView = input<undefined | boolean>(false);
  step = input<number>();
  fiatPurchaseEnabled = input<boolean>(false);
  cryptoPurchaseEnabled = input<boolean>(false);
  initialAmount = input<number | null>(null);
  /** Set by the dialog after a payment child hit the 403 — `/self` may still read under the cap. */
  purchaseLimitReached = input<boolean>(false);
  selectPackage = output<{
    selection: PurchaseSelection;
    paymentMethod: PurchasePaymentMethod;
    forceKyc?: boolean;
    savedCard?: PaymentMethod<CoinflowCardMetadata>;
  }>();
  close = output<void>();
  private _amountInput = viewChild<ElementRef<HTMLInputElement>>('customAmountInput');

  // Form Controls
  amount = new FormControl('', {
    nonNullable: true,
    validators: [Validators.min(9.99)],
  });

  // Observable to Signal Conversions
  user = toSignal(this._userView.state$);
  activePurchaseBoost = toSignal(this._promotionsView.activePurchaseBoost$);
  private _allPaymentMethods = toSignal(this._paymentMethodsView.visiblePaymentMethods$);
  lifetimePurchaseLimits = toSignal(
    this._accountLimitsView.lifetimePurchaseLimits$
  );
  private _amountValue = toSignal(this.amount.valueChanges);

  // Promotions state
  private _promotionsState = toSignal(this._promotionsView.state$);
  promosExpanded = signal(false);
  promotionBeingClaimed = signal<string | null>(null);

  /** Whether the boost detail view is shown (FTP tier view or regular boost info) */
  showBoostInfoView = signal(false);

  // Preset amounts — switch to tier-aligned presets for first-time boost
  readonly presetAmounts = computed(() =>
    this.activePurchaseBoost()?.isFirstTimePurchaseBoost
      ? FIRST_TIME_PRESET_AMOUNTS
      : PRESET_AMOUNTS
  );


  /** Enriched FTP pill data with tier tag, color, and bonus — null when not FTP */
  readonly ftpPills = computed(() => {
    const boost = this.activePurchaseBoost();
    if (!boost?.isFirstTimePurchaseBoost || !boost.tiers?.length) return null;

    return FIRST_TIME_PRESET_AMOUNTS.map((amount, i) => {
      const meta = FTP_TIER_METAS[i];
      const tier = boost.tiers!.find(t => Number(t.tier_price_usd) === amount);
      const bonusPercent = tier ? Math.round(Number(tier.bonus_percentage)) : null;
      return { amount, label: meta?.label ?? null, color: meta?.color ?? null, bonusPercent };
    });
  });

  // Computed Signals
  readonly maxAmountUsd = MAX_PURCHASE_AMOUNT_USD;

  customAmount = computed(() =>
    decimal(
      (this._amountValue()?.trim().length ? this._amountValue()! : '0').replace(
        /\.$/,
        ''
      )
    )
  );

  customAmountSelection = computed(() =>
    this._calculateSelection(this.customAmount().toNumber())
  );

  isLoading = computed(
    () => !this.user()?.kyc_verified && !this.lifetimePurchaseLimits()
  );

  /** All available (unclaimed) purchase-boost promotions */
  availablePromotions = computed(() => {
    const state = this._promotionsState();
    if (!state) return [];
    return state.promotions.filter(p =>
      !p.claimed_at &&
      !p.used_at &&
      p.rewards.some(r => isBoostRewardType(r.promotion_reward_type_id))
    );
  });

  /** Count of available (unclaimed) promotions for the banner */
  availablePromoCount = computed(() => this.availablePromotions().length);

  /** Whether to show the green "promos available" banner (no active boost, but promos exist) */
  showPromoBanner = computed(() =>
    !this.activePurchaseBoost() && this.availablePromoCount() > 0
  );

  /** The max boost SC from the highest tier (first-time) or percentage (regular) */
  activeBoostPercentage = computed(() => {
    const boost = this.activePurchaseBoost();
    if (!boost) return 0;
    // For first-time boost, percentage is not applicable (pseudo-package model)
    return boost.percentage;
  });

  /** Boost eligibility text showing min and/or max range (mirrors native _boostEligibilityText) */
  boostMaxPurchaseText = computed(() => {
    const boost = this.activePurchaseBoost();
    if (!boost || boost.isFirstTimePurchaseBoost) return null;

    const min = boost.min_boost_eligibility_amount_usd ? Number(boost.min_boost_eligibility_amount_usd) : null;
    const max = boost.max_boost_eligibility_amount_usd
      ? Number(boost.max_boost_eligibility_amount_usd)
      : boost.fixed_boost_amount_usd
        ? Number(boost.fixed_boost_amount_usd)
        : null;

    if (min != null && max != null) {
      return `Boost applies on amounts $${min.toFixed(0)} \u2013 $${max.toFixed(0)}`;
    } else if (max != null) {
      return `Boost applies up to $${max.toFixed(0)}`;
    } else if (min != null) {
      return `Boost minimum $${min.toFixed(0)}`;
    }
    return null;
  });

  /** Inline warning when purchase amount exceeds boost cap */
  boostCapExceededWarning = computed(() => {
    const boost = this.activePurchaseBoost();
    if (!boost || boost.isFirstTimePurchaseBoost) return null;

    const cap = boost.max_boost_eligibility_amount_usd
      ? Number(boost.max_boost_eligibility_amount_usd)
      : boost.fixed_boost_amount_usd
        ? Number(boost.fixed_boost_amount_usd)
        : null;
    if (cap == null) return null;

    const amount = this.customAmount().toNumber();
    if (amount <= cap) return null;
    return `Boost applies to the first $${cap.toFixed(0)} only. Any excess amount above will receive standard rates.`;
  });

  /** Whether the amount is valid enough to select a payment method */
  canSelectPaymentMethod = computed(() => {
    const amt = this.customAmount();
    return amt.gt(0) && amt.gte(10) && amt.lte(this.maxAmountUsd);
  });

  /** Saved credit cards available for purchase */
  savedCards = computed(
    () =>
      (this._allPaymentMethods()?.filter(
        (p) =>
          p.visible_to_user &&
          p.payment_method_entity_type_id ===
            PaymentMethodEntityTypeId.CoinflowCardToken
      ) ?? []) as PaymentMethod<CoinflowCardMetadata>[]
  );

  // OFF by default. When true, saved-card selection is blocked behind
  // a spinner until the Coinflow purchase-method reconcile confirms the tokens
  // are fresh, so a card whose token was deleted server-side can't be picked.
  // We keep it off because fast purchase-screen load beats the stale-token race
  // (an accepted business tradeoff since packages were removed) — but all the
  // plumbing (pmSyncState + the reconcile/refresh pipeline below) stays wired so
  // flipping this to true (or later reading a casino setting, e.g. the
  // off-by-default CasinoSettingKeyId.PurchaseLockAutoReleaseEnabled precedent)
  // re-enables blocking with no other changes.
  private readonly PURCHASE_SYNC_BLOCKING_ENABLED = false;

  // Tracks the Coinflow purchase-method reconcile that runs on init. The POST
  // always fires (fire-and-forget when blocking is off) and refreshes the shared
  // list; 'stale'/error fail open. Only gates the picker when blocking is on.
  pmSyncState = signal<'syncing' | 'fresh' | 'stale'>('syncing');
  savedCardsSyncing = computed(
    () => this.PURCHASE_SYNC_BLOCKING_ENABLED && this.pmSyncState() === 'syncing',
  );

  /** Card payment enabled (fiat purchases enabled for this user) */
  cardPaymentEnabled = computed(() => this.fiatPurchaseEnabled());

  /** Fiat methods are present but locked — render rows greyed with "Unavailable" chip */
  fiatRowsDisabled = computed(() => !this.fiatPurchaseEnabled());

  /** At the state's daily purchase cap — every pay action is locked and the copy takes the validation slot */
  purchaseCapReached = computed(() => this.purchaseLimitReached() || this._deviceModeView.purchaseCapReached());
  readonly purchaseCapMessage = PURCHASE_LIMIT_REACHED_MESSAGE;

  constructor() {
    this.amount.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.showPaymentMethodError.set(false);
    });

    // Synchronously reconcile the user's saved Coinflow purchase cards against
    // Coinflow's live state before they can pick a (possibly deleted) token.
    // The POST reconciled the DB; we then refresh the full shared state via
    // syncPaymentMethods() (GET-all) rather than setPaymentMethods() so other
    // methods aren't wiped. Fail open: stale:true or any error unblocks with
    // the cached list.
    from(this._paymentMethodsService.syncCoinflowPurchasePaymentMethods())
      .pipe(
        switchMap((response) =>
          from(this._paymentMethodsView.syncPaymentMethods()).pipe(
            map(() => (response.stale ? 'stale' : 'fresh')),
            catchError((err) => {
              this._logger.error('Failed to refresh payment methods after Coinflow sync', { error: err });
              return of('stale' as const);
            }),
          ),
        ),
        catchError((err) => {
          this._logger.error('Failed to sync Coinflow purchase payment methods', { error: err });
          return of('stale' as const);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((state) => this.pmSyncState.set(state));

    // Focus the amount input as soon as it lands in the DOM. The dialog
    // opens with MatDialog autoFocus disabled so we own focus management
    // here — that avoids the race where MatDialog targets the close button
    // when the input is still gated behind isLoading(). afterRenderEffect
    // runs after each render with the DOM guaranteed up-to-date, so the
    // first render that includes the input fires this hook.
    let hasFocused = false;
    afterRenderEffect({
      write: () => {
        if (hasFocused) return;
        const input = this._amountInput();
        if (!input) return;
        hasFocused = true;
        input.nativeElement.focus();
      },
    });

    afterNextRender(() => {
      const initial = this.initialAmount();
      if (initial != null && initial > 0) {
        this.amount.setValue(String(initial));
        this._formatInputDisplay();
      }
      this._analytics.trackEvent(AnalyticsEvent.BuyCoinsOpened, {});
    });
  }

  private _calculateSelection(inputAmountNum: number): PurchaseSelection {
    const inputAmount = decimal(inputAmountNum);
    const boost = this.activePurchaseBoost();

    // Direct conversion: no packages
    const baseGC = inputAmount.mul(GOLD_CONVERSION_RATE);
    const baseSC = inputAmount.mul(SWEEPS_CONVERSION_RATE);

    let boostSC = decimal(0);
    let boostGC = decimal(0);

    if (boost) {
      if (boost.isFirstTimePurchaseBoost && boost.tiers?.length) {
        // First-time boost: percentage-based tiers.
        // bonus_percentage applies only to the tier_price_usd portion;
        // any excess above the tier price gets the default rate (no boost).
        const matchedTier = this._resolveFirstTimeBoostTier(inputAmountNum, boost.tiers);
        if (matchedTier) {
          const tierPrice = decimal(matchedTier.tier_price_usd);
          const bonusPct = decimal(matchedTier.bonus_percentage).div(100);
          boostSC = tierPrice.mul(SWEEPS_CONVERSION_RATE).mul(bonusPct);
          boostGC = tierPrice.mul(GOLD_CONVERSION_RATE).mul(bonusPct);

          if (boost.max_boost_award_gc != null) {
            boostGC = Decimal.min(boostGC, decimal(boost.max_boost_award_gc));
          }
          if (boost.max_boost_award_sc != null) {
            boostSC = Decimal.min(boostSC, decimal(boost.max_boost_award_sc));
          }
        }
      } else if (
        boost.percentage > 0 &&
        (!boost.min_boost_eligibility_amount_usd ||
          inputAmount.gte(decimal(boost.min_boost_eligibility_amount_usd)))
      ) {
        // Regular boost: percentage-based
        const boostPercentage = decimal(boost.percentage).div(100);

        const boostCap = boost.max_boost_eligibility_amount_usd ?? boost.fixed_boost_amount_usd;
        const boostableAmount = boostCap
          ? Decimal.min(inputAmount, decimal(boostCap))
          : inputAmount;

        boostGC = boostableAmount.mul(GOLD_CONVERSION_RATE).mul(boostPercentage);
        boostSC = boostableAmount.mul(SWEEPS_CONVERSION_RATE).mul(boostPercentage);

        if (boost.max_boost_award_gc != null) {
          boostGC = Decimal.min(boostGC, decimal(boost.max_boost_award_gc));
        }
        if (boost.max_boost_award_sc != null) {
          boostSC = Decimal.min(boostSC, decimal(boost.max_boost_award_sc));
        }
      }
    }

    const selection: PurchaseSelection = {
      inputAmount,
      effectiveAmount: inputAmount,
      boostSC,
      boostGC,
      scAwarded: baseSC.add(boostSC),
      gcAwarded: baseGC.add(boostGC),
    };

    this._logger.info(
      'Calculated purchase selection',
      JSON.parse(JSON.stringify(selection))
    );

    return selection;
  }

  selectPreset(preset: number) {
    this.amount.setValue(String(preset));
    this._formatInputDisplay();
  }

  /** Handle tier selection from the First Time Boost detail view */
  handleBoostTierSelect(amount: number) {
    this.showBoostInfoView.set(false);
    this.selectPreset(amount);
  }

  /** Whether to show the "enter a valid amount" error on the payment methods */
  showPaymentMethodError = signal(false);

  selectPaymentMethod(method: PurchasePaymentMethod) {
    if (this.purchaseCapReached()) {
      return;
    }

    if (method !== 'crypto' && this.fiatRowsDisabled()) {
      return;
    }

    if (!this.canSelectPaymentMethod()) {
      this.amount.markAsTouched();
      this.showPaymentMethodError.set(true);
      return;
    }

    const amountNum = this.customAmount().toNumber();
    const selection = this._calculateSelection(amountNum);
    this._emitSelection(selection, method);
  }

  selectSavedCard(card: PaymentMethod<CoinflowCardMetadata>) {
    if (this.purchaseCapReached() || this.fiatRowsDisabled()) {
      return;
    }

    // Block selection until the Coinflow reconcile resolves so a card with a
    // dead token can't be picked. Defensive — the rows are also disabled in the
    // template while syncing.
    if (this.savedCardsSyncing()) {
      return;
    }

    if (!this.canSelectPaymentMethod()) {
      this.amount.markAsTouched();
      this.showPaymentMethodError.set(true);
      return;
    }

    const amountNum = this.customAmount().toNumber();
    const selection = this._calculateSelection(amountNum);
    this._emitSelection(selection, 'card', card);
  }

  private _emitSelection(
    selection: PurchaseSelection,
    paymentMethod: PurchasePaymentMethod,
    savedCard?: PaymentMethod<CoinflowCardMetadata>,
  ) {
    const hasHadPurchase = this.user()?.has_had_purchase;

    if (!hasHadPurchase) {
      this._analytics.trackEvent('ftp_started', {
        session_id: generateId(),
        value: selection.inputAmount.toNumber(),
        currency: 'USD',
        item_id: this._resolveAnalyticsItemId(selection.inputAmount.toNumber()),
      });
    }

    this._analytics.trackEvent('purchase_started', {
      item_id: this._resolveAnalyticsItemId(selection.inputAmount.toNumber()),
      price: selection.inputAmount.toNumber(),
      currency: 'USD',
    });

    let forceKyc = false;

    if (!this.user()?.kyc_verified) {
      const limits = this.lifetimePurchaseLimits();
      // Zero is a real limit: the server reports it for an account owed
      // identity verification whatever the amount.
      if (limits?.lifetime_purchase_usd_limit != null) {
        const lifetimeCompletedPurchasesUSD = decimal(
          limits.lifetime_purchase_amount_usd ?? 0
        );

        forceKyc = lifetimeCompletedPurchasesUSD
          .add(selection.inputAmount)
          .gt(limits.lifetime_purchase_usd_limit);
      }
    }

    this.selectPackage.emit({ selection, paymentMethod, forceKyc, savedCard });
  }

  /** Toggle promotions list expansion */
  togglePromosExpanded() {
    this.promosExpanded.update(v => !v);
  }

  /** Claim (activate) a promotion from the inline list */
  async claimPromotion(promotionId: string) {
    this.promotionBeingClaimed.set(promotionId);

    try {
      await lastValueFrom(
        this._http
          .post(`${environment.apiUrl}/promotions/${promotionId}/claim`, {})
          .pipe(take(1))
      );

      await this._promotionsView.syncPromotions();
      this.promosExpanded.set(false);
    } catch (error: any) {
      const errorCode = error?.error?.code;

      if (errorCode === 'PROMOTION_ALREADY_CLAIMED') {
        this._alertService.notifyWarning('Already Activated', 'This promotion has already been activated.');
      } else {
        this._alertService.notifyDanger('Activation Failed', 'Failed to activate promotion. Please try again.');
      }
    } finally {
      this.promotionBeingClaimed.set(null);
    }
  }

  /** Extract the boost percentage from a promotion's rewards */
  getBoostPercentage(promo: UserPromotionRewardVM): number {
    const boostReward = promo.rewards.find(r => isBoostRewardType(r.promotion_reward_type_id));
    if (!boostReward) return 0;
    const metadata = boostReward.metadata as { percentage?: string };
    return Number(metadata?.percentage ?? 0);
  }

  /** Extract max tier bonus percentage from a first-time purchase boost promotion */
  getBoostTierRange(promo: UserPromotionRewardVM): number | null {
    const boostReward = promo.rewards.find(r =>
      r.promotion_reward_type_id === PromotionRewardType.FirstTimePurchaseBoost
    );
    if (!boostReward) return null;
    const metadata = boostReward.metadata as { tiers?: FirstTimeBoostTier[] };
    if (!metadata?.tiers?.length) return null;
    return Math.max(...metadata.tiers.map(t => Number(t.bonus_percentage)));
  }

  /** Extract max boost amount from a promotion's rewards for subtitle text */
  getBoostMaxAmount(promo: UserPromotionRewardVM): string | null {
    const boostReward = promo.rewards.find(r => isBoostRewardType(r.promotion_reward_type_id));
    if (!boostReward) return null;
    const metadata = boostReward.metadata as { fixed_boost_amount_usd?: string | null };
    return metadata?.fixed_boost_amount_usd
      ? `$${Number(metadata.fixed_boost_amount_usd).toFixed(2)}`
      : null;
  }

  amountKeydown(event: KeyboardEvent) {
    if (
      event.key.startsWith('Arrow') ||
      ['Home', 'End', 'Tab', 'Escape'].includes(event.key) ||
      event.ctrlKey || event.metaKey
    ) {
      return;
    }

    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const displayValue = input.value;
    const fStart = input.selectionStart ?? displayValue.length;
    const fEnd = input.selectionEnd ?? fStart;

    // Work on raw value (no commas) so validation stays simple
    const raw = displayValue.replace(/,/g, '');
    const start = this._toRawPos(displayValue, fStart);
    const end = this._toRawPos(displayValue, fEnd);
    let value: string;
    let rawCaret: number;

    if (event.key === 'Backspace') {
      if (start === end) {
        if (start === 0) return;
        value = raw.slice(0, start - 1) + raw.slice(end);
        rawCaret = start - 1;
      } else {
        value = raw.slice(0, start) + raw.slice(end);
        rawCaret = start;
      }
    } else if (event.key === 'Delete') {
      value = start === end
        ? raw.slice(0, start) + raw.slice(end + 1)
        : raw.slice(0, start) + raw.slice(end);
      rawCaret = start;
    } else if (event.key.length === 1) {
      value = raw.slice(0, start) + event.key + raw.slice(end);
      rawCaret = start + 1;
    } else {
      return;
    }

    if (value !== '' && !value.match(/^(0|[1-9][0-9]*)(\.[0-9]{0,2})?$/)) {
      return;
    }

    const maxLength = value.includes('.')
      ? value.split('.')[1].length === 1 ? 7 : 8
      : 5;

    if (value.length > maxLength) return;

    const formatted = this._addGrouping(value);
    const caretPos = this._toFormattedPos(formatted, rawCaret);

    this.amount.setValue(value);
    input.value = formatted;
    input.setSelectionRange(caretPos, caretPos);
  }

  private _addGrouping(raw: string): string {
    if (!raw) {
      return '';
    }

    const [intPart, decPart] = raw.split('.');
    const grouped = formatCurrency(decimal(intPart).toNumber(), 'en-US', '', 'USD', '1.0-0');
    return decPart != null ? `${grouped}.${decPart}` : grouped;
  }

  private _toRawPos(formatted: string, fPos: number): number {
    let raw = 0;
    for (let i = 0; i < fPos; i++) {
      if (formatted[i] !== ',') raw++;
    }
    return raw;
  }

  private _toFormattedPos(formatted: string, rawPos: number): number {
    let raw = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== ',') {
        if (raw === rawPos) return i;
        raw++;
      }
    }
    return formatted.length;
  }

  private _formatInputDisplay() {
    const el = this._amountInput()?.nativeElement;
    if (el) {
      el.value = this._addGrouping(this.amount.value);
    }
  }

  /**
   * Resolves the matching first-time boost tier for a given amount.
   * Matches backend logic: sort tiers by tier_price_usd descending,
   * return the first tier where amount >= tier_price_usd.
   */
  private _resolveFirstTimeBoostTier(amountUsd: number, tiers: FirstTimeBoostTier[]): FirstTimeBoostTier | null {
    const sorted = [...tiers].sort(
      (a, b) => Number(b.tier_price_usd) - Number(a.tier_price_usd)
    );

    for (const tier of sorted) {
      if (amountUsd >= Number(tier.tier_price_usd)) {
        return tier;
      }
    }

    return null;
  }

  private _resolveAnalyticsItemId(amountUsd: any): string {
    const isFirstTime = this.activePurchaseBoost()?.isFirstTimePurchaseBoost;
    if (isFirstTime) {
      return (FIRST_TIME_PRESET_AMOUNTS as readonly number[]).includes(amountUsd)
        ? `ftp_${amountUsd}`
        : 'ftp_custom';
    }

    return (PRESET_AMOUNTS as readonly number[]).includes(amountUsd)
      ? `purchase_preset_${amountUsd}`
      : 'purchase_custom';
  }
}
